import { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronLeft, Mail, MapPin, ExternalLink, Home, Star, User as UserIcon, LogOut, LogIn, LayoutDashboard, ClipboardList, Calendar } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// V2 Theme Colors
const C = {
  white: '#FFFFFF',
  cloud: '#F8FAFC',
  sky: '#0EA5E9',
  skyLight: '#E0F2FE',
  skyDark: '#0284C7',
  ink: '#0F172A',
  slate: '#475569',
  muted: '#94A3B8',
};

const SiteHeaderV2 = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const closeTimeoutRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    navigate('/', { replace: true });
  };

  // On the aquatic home page the header sits over a dark blue bg -> transparent + light text
  const isHome = location.pathname === '/respiration';
  const isCabinetHome = location.pathname === '/';
  const APPOINTMENT_LINK = 'https://book.agenda.ch/services?companyId=19135';
  // /respiration now uses the same light aquatic bg as /, so pretend both are "light" pages
  const isLightBg = true; // both / and /respiration use light bg — dark nav text
  const headerBg = isLightBg ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.08)';
  const headerBorder = isLightBg ? '1px solid rgba(255,255,255,0.4)' : '1px solid rgba(255,255,255,0.15)';
  const navTextColor = isLightBg ? C.slate : '#FFFFFF';
  const navActiveColor = isLightBg ? C.sky : '#FFFFFF';
  const nameTextColor = isLightBg ? C.ink : '#FFFFFF';
  const subTextColor = isLightBg ? '#475569' : 'rgba(255,255,255,0.85)';
  
  const handleMouseEnter = (index) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setDropdownOpen(index);
  };
  
  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(null);
    }, 200);
  };
  
  const menuItems = [
    { path: "/respiration", label: "Respiration", type: "link" },
    {
      label: "Troubles respiratoires",
      type: "dropdown",
      items: [
        { path: "/hyperventilation", label: "Hyperventilation" },
        { path: "/respiration-dysfonctionnelle", label: "Respiration Dysfonctionnelle" },
        { path: "/asthme", label: "Asthme" },
        { path: "/covid-long", label: "COVID Long" },
      ]
    },
    {
      label: "Ressources",
      type: "dropdown",
      items: [
        { path: "/causes-troubles-respiratoires", label: "Causes des Troubles" },
        { path: "/mecanique-respiratoire", label: "Mécanique Respiratoire" },
        { path: "/respiration-optimale", label: "Respiration Optimale" },
        { path: "/nerf-vague", label: "Nerf Vague" },
      ]
    },
    {
      label: "Techniques",
      type: "dropdown",
      items: [
        { path: "/coherence-cardiaque", label: "Cohérence Cardiaque" },
        { path: "/methode-buteyko", label: "Méthode Buteyko" },
        { path: "/mobilite-mouvement", label: "Mobilité et Mouvement" },
        { path: "/entrainement-muscles-respiratoires", label: "Entraînement Muscles Respiratoires" },
      ]
    },
    { path: "/evaluer-respiration", label: "Tester", type: "link" },
    { path: "/documents", label: "Documents", type: "link" },
    { path: "/youtube", label: "Youtube", type: "link" },
  ];

  return (
    <>
      <header
        className="sticky top-0 z-50"
        style={{
          backgroundColor: headerBg,
          backdropFilter: 'saturate(180%) blur(24px)',
          WebkitBackdropFilter: 'saturate(180%) blur(24px)',
          borderBottom: headerBorder,
          boxShadow: isLightBg ? '0 8px 32px rgba(0,0,0,0.04)' : '0 8px 32px rgba(0,0,0,0.15)',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12 relative">
          {/* Certified Instructor badge — centered on homepage, links to Oxygen Advantage */}
          {isCabinetHome && (
            <div
              className="flex absolute inset-0 items-center justify-center pointer-events-none"
            >
              <a
                href="https://oxygenadvantage.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Oxygen Advantage - Certified Instructor"
                title="Oxygen Advantage · Certified Instructor"
                className="pointer-events-auto transition-transform hover:scale-105"
                data-testid="header-oa-badge"
              >
                <img
                  src="https://customer-assets-lqy194kg.emergentagent.net/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/0aqm7wkg_Watermark-Certified-Instructor.png"
                  alt="Oxygen Advantage Certified Instructor"
                  className="h-8 sm:h-11 lg:h-14 opacity-95"
                  style={{
                    objectFit: 'cover',
                    objectPosition: 'center bottom',
                    aspectRatio: '1080 / 250',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))'
                  }}
                />
              </a>
            </div>
          )}
          {/* Desktop Header */}
          <div className="hidden lg:flex justify-between items-center py-4">
            {/* Logo & Name — only on the cabinet homepage */}
            <div className="flex items-center gap-4">
              {isCabinetHome ? (
                <>
                  <button onClick={() => setProfileModalOpen(true)} className="group relative">
                    <img
                      src="https://customer-assets.emergentagent.com/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/c1aoalsm_ChatGPT%20Image%2026%20juin%202026%2C%2014_21_40.png"
                      alt="Julien Jaccard"
                      className="w-14 h-14 rounded-full object-cover transition-all group-hover:ring-4 group-hover:ring-sky-100"
                      style={{ border: `2px solid ${C.sky}`, objectPosition: 'center 20%' }}
                    />
                  </button>
                  <div>
                    <button onClick={() => setProfileModalOpen(true)} className="text-left">
                      <p className="text-lg font-medium hover:text-sky-600 transition-colors whitespace-nowrap" style={{ fontFamily: 'Outfit, sans-serif', color: nameTextColor }}>
                        Julien Jaccard
                      </p>
                      <p className="text-xs font-medium whitespace-nowrap" style={{ color: subTextColor }}>Physiothérapeute</p>
                    </button>
                  </div>
                </>
              ) : (
                /* Placeholder to keep the flex spacing symmetric on non-home pages */
                <span aria-hidden="true" />
              )}
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {isCabinetHome ? (
                <a
                  href={APPOINTMENT_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 whitespace-nowrap"
                  style={{ backgroundColor: '#0EA5E9', color: '#FFFFFF', boxShadow: '0 6px 18px -6px rgba(14,165,233,0.5)' }}
                  data-testid="header-appointment-cta"
                >
                  <Calendar size={16} />
                  Prendre rendez-vous
                </a>
              ) : (<>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all hover:shadow-lg hover:-translate-y-0.5 whitespace-nowrap"
                  style={{
                    background: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
                    color: '#FFFFFF',
                    boxShadow: '0 4px 12px rgba(14,165,233,0.35)',
                  }}
                  data-testid="header-back-to-cabinet"
                  title="Retour au site du cabinet"
                >
                  <ChevronLeft size={16} />
                  Cabinet
                </Link>
                {menuItems.map((item, index) => {
                if (item.type === 'home') {
                  const active = location.pathname === '/';
                  return (
                    <Link
                      key="home"
                      to="/"
                      className="p-2 rounded-full transition-colors hover:bg-sky-100"
                      style={{
                        color: active ? navActiveColor : navTextColor,
                        textShadow: 'none',
                        backgroundColor: active ? 'rgba(14,165,233,0.12)' : 'transparent',
                      }}
                      title="Accueil"
                      aria-label="Accueil"
                      data-testid="header-home-button"
                    >
                      <Home size={20} />
                    </Link>
                  );
                }
                if (item.type === 'link' && item.path === '/youtube') {
                  const active = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 whitespace-nowrap"
                      style={{ backgroundColor: '#DC2626', color: '#FFFFFF', boxShadow: active ? '0 6px 18px -6px rgba(220,38,38,0.6)' : '0 3px 10px -3px rgba(220,38,38,0.45)' }}
                      data-testid="header-youtube-button"
                    >
                      {item.label}
                    </Link>
                  );
                }
                return item.type === 'link' ? (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="text-base font-medium transition-colors hover:text-sky-300"
                    style={{ color: location.pathname === item.path ? navActiveColor : navTextColor, textShadow: 'none' }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <div key={item.label} className="relative" onMouseEnter={() => handleMouseEnter(index)} onMouseLeave={handleMouseLeave}>
                    <button 
                      className="flex items-center gap-1.5 text-base font-medium transition-colors hover:text-sky-300"
                      style={{ color: navTextColor, textShadow: 'none' }}
                    >
                      {item.label}
                      <ChevronDown size={14} className={`transition-transform duration-300 ${dropdownOpen === index ? 'rotate-180' : ''}`} />
                    </button>
                    {dropdownOpen === index && (
                      <div className="absolute top-full left-0 pt-4 w-64 z-50">
                        <div
                          className="header-dropdown rounded-2xl py-3 overflow-hidden"
                          style={{
                            backgroundColor: '#FFFFFF',
                            border: '1px solid rgba(226,232,240,0.8)',
                            boxShadow: '0 20px 50px -15px rgba(0,0,0,0.15)',
                          }}
                        >
                          {item.items.map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setDropdownOpen(null)}
                              className="header-dropdown-item block px-5 py-2.5 text-sm transition-all"
                              style={{ color: location.pathname === subItem.path ? '#0EA5E9' : '#475569' }}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              </>)}
              {/* User menu / login button — desktop, hidden on cabinet homepage */}
              {!isCabinetHome && (user ? (
                <div className="relative" data-testid="header-user-menu-wrapper">
                  <button
                    type="button"
                    onClick={() => setUserMenuOpen((v) => !v)}
                    className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium border transition-colors"
                    style={{
                      color: navTextColor,
                      borderColor: isHome ? 'rgba(255,255,255,0.35)' : 'rgba(2,132,199,0.4)',
                      backgroundColor: isHome ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.5)',
                    }}
                    aria-label="Mon profil"
                    title={user.name}
                    data-testid="header-user-menu-btn"
                  >
                    <UserIcon size={16} />
                    {!isCabinetHome && (
                      <span className="max-w-[110px] truncate">{user.name || 'Mon profil'}</span>
                    )}
                    <ChevronDown size={14} className={`transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50" data-testid="header-user-menu-dropdown">
                      <div className="px-4 py-2 border-b border-slate-100">
                        <p className="text-sm font-semibold text-slate-900 truncate">{user.name}</p>
                        <p className="text-xs text-slate-500 truncate">{user.email}</p>
                      </div>
                      <Link
                        to="/mon-espace"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-sky-700 transition-colors"
                        data-testid="header-mon-espace-btn"
                      >
                        <LayoutDashboard size={16} />
                        Mon espace
                      </Link>
                      <Link
                        to="/mon-espace/mon-programme"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-sky-700 transition-colors"
                        data-testid="header-mon-programme-btn"
                      >
                        <ClipboardList size={16} />
                        Mon programme
                      </Link>
                      <Link
                        to="/mon-espace/programme"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-sky-700 transition-colors"
                        data-testid="header-weekly-program-btn"
                      >
                        <ClipboardList size={16} />
                        Création de programme
                      </Link>
                      <Link
                        to="/breathing"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-sky-700 transition-colors"
                        data-testid="header-exercises-hub-btn"
                      >
                        <Home size={16} />
                        Accueil des exercices
                      </Link>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        data-testid="header-logout-btn"
                      >
                        <LogOut size={16} />
                        Se déconnecter
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium border transition-colors"
                  style={{
                    color: navTextColor,
                    borderColor: isHome ? 'rgba(255,255,255,0.35)' : 'rgba(2,132,199,0.4)',
                    backgroundColor: isHome ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.5)',
                  }}
                  data-testid="header-login-link"
                >
                  <LogIn size={16} />
                  Se connecter
                </Link>
              ))}
            </nav>
          </div>
          <div className="lg:hidden grid grid-cols-3 items-center py-4">
            {/* Photo cabinet à gauche — visible uniquement sur la page cabinet */}
            <div className="justify-self-start">
              {isCabinetHome ? (
                <button onClick={() => setProfileModalOpen(true)} className="group relative">
                  <img
                    src="https://customer-assets.emergentagent.com/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/c1aoalsm_ChatGPT%20Image%2026%20juin%202026%2C%2014_21_40.png"
                    alt="Julien Jaccard"
                    className="w-12 h-12 rounded-full object-cover transition-all group-hover:ring-4 group-hover:ring-sky-100"
                    style={{ border: `2px solid ${C.sky}`, objectPosition: 'center 20%' }}
                  />
                </button>
              ) : (
                <span aria-hidden="true" />
              )}
            </div>

            {/* Logo horizontal centré — même logo que l'app d'exercices (/breathing). */}
            <div className="justify-self-center">
              {!isCabinetHome && (
                <Link to="/respiration" aria-label="Physiothérapie Les Mains Nuages" data-testid="mobile-header-brand-logo" className="flex items-center">
                  <img
                    src="/logo-mains-nuages.png"
                    alt="Physiothérapie Les Mains Nuages"
                    className="h-11 sm:h-12 w-auto max-w-[220px] object-contain"
                    style={{ filter: 'drop-shadow(0 1px 2px rgba(15,32,64,0.15))' }}
                  />
                </Link>
              )}
            </div>

            {/* Menu hamburger à droite */}
            <div className="justify-self-end">
              <button 
                className="p-2 rounded-xl hover:bg-white/20 transition-colors" 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                style={{ color: navTextColor }}
                aria-label="Menu"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav 
              className="lg:hidden py-6 border-t border-white/10 -mx-4 sm:-mx-6 px-4 sm:px-6 rounded-b-2xl"
              style={{
                background: 'linear-gradient(180deg, rgba(15,32,64,0.85) 0%, rgba(10,25,49,0.92) 100%)',
                backdropFilter: 'blur(24px) saturate(1.2)',
                WebkitBackdropFilter: 'blur(24px) saturate(1.2)',
                boxShadow: '0 24px 48px -12px rgba(0,0,0,0.4)',
              }}
            >
              <div className="flex flex-col gap-2">
                {isCabinetHome ? (<>
                  <a
                    href={APPOINTMENT_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    className="mx-4 my-2 px-5 py-4 rounded-full text-base font-semibold text-center shadow-lg inline-flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#0EA5E9', color: '#FFFFFF' }}
                    data-testid="mobile-appointment-cta"
                  >
                    <Calendar size={18} />
                    Prendre rendez-vous
                  </a>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      window.dispatchEvent(new Event('open-leave-review'));
                    }}
                    className="mx-4 my-2 px-5 py-4 rounded-full text-base font-medium text-center flex items-center justify-center gap-2 border transition-colors"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.3)' }}
                    data-testid="mobile-leave-review-btn"
                  >
                    <Star size={16} className="fill-amber-400 text-amber-400" />
                    Laisser un avis
                  </button>
                </>) : (<>
                  <Link
                    to="/"
                    onClick={() => setMobileMenuOpen(false)}
                    className="mx-4 my-2 px-4 py-3 rounded-full text-sm font-medium text-center border transition-colors hover:bg-white/10"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.3)' }}
                    data-testid="mobile-back-to-cabinet"
                  >
                    ← Retour au cabinet
                  </Link>
                  {menuItems.map((item, index) => {
                  if (item.type === 'home') {
                    return (
                      <Link
                        key="home-mobile"
                        to="/"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-4 rounded-xl text-lg font-medium hover:bg-white/10"
                        style={{ color: '#FFFFFF', textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}
                      >
                        <Home size={20} />
                        Accueil
                      </Link>
                    );
                  }
                  if (item.type === 'link' && item.path === '/youtube') {
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className="mx-4 my-1 px-4 py-3 rounded-full text-base font-medium text-center"
                        style={{ backgroundColor: '#DC2626', color: '#FFFFFF', boxShadow: '0 4px 14px -4px rgba(220,38,38,0.6)' }}
                      >
                        {item.label}
                      </Link>
                    );
                  }
                  return item.type === 'link' ? (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="px-4 py-4 rounded-xl text-lg font-medium hover:bg-white/10"
                      style={{ color: '#FFFFFF', textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <div key={item.label}>
                      <button
                        onClick={() => setDropdownOpen(dropdownOpen === index ? null : index)}
                        className="flex items-center justify-between w-full px-4 py-4 rounded-xl text-lg font-medium hover:bg-white/10"
                        style={{ color: '#FFFFFF', textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}
                      >
                        {item.label}
                        <ChevronDown size={18} className={`transition-transform ${dropdownOpen === index ? 'rotate-180' : ''}`} />
                      </button>
                      {dropdownOpen === index && (
                        <div className="pl-6 mt-1 space-y-1">
                          {item.items.map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block px-4 py-3 rounded-xl text-base hover:bg-white/10"
                              style={{ color: location.pathname === subItem.path ? '#7DD3FC' : 'rgba(255,255,255,0.85)', textShadow: '0 1px 3px rgba(0,0,0,0.35)' }}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                </>)}
                {/* Mobile user menu — hidden on cabinet homepage */}
                {!isCabinetHome && (
                <div className="px-4 mt-4 pt-4 border-t border-white/10">
                  {user ? (
                    <>
                      <div className="px-1 pb-3">
                        <p className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>{user.name}</p>
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{user.email}</p>
                      </div>
                      <Link
                        to="/mon-espace"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center justify-center gap-2 w-full px-6 py-3 mb-3 rounded-full text-sm font-medium border transition-colors"
                        style={{ color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.35)', backgroundColor: 'rgba(255,255,255,0.08)' }}
                        data-testid="mobile-mon-espace-btn"
                      >
                        <LayoutDashboard size={16} />
                        Mon espace
                      </Link>
                      <Link
                        to="/mon-espace/mon-programme"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center justify-center gap-2 w-full px-6 py-3 mb-3 rounded-full text-sm font-medium border transition-colors"
                        style={{ color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.35)', backgroundColor: 'rgba(255,255,255,0.08)' }}
                        data-testid="mobile-mon-programme-btn"
                      >
                        <ClipboardList size={16} />
                        Mon programme
                      </Link>
                      <Link
                        to="/mon-espace/programme"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center justify-center gap-2 w-full px-6 py-3 mb-3 rounded-full text-sm font-medium border transition-colors"
                        style={{ color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.35)', backgroundColor: 'rgba(255,255,255,0.08)' }}
                        data-testid="mobile-weekly-program-btn"
                      >
                        <ClipboardList size={16} />
                        Création de programme
                      </Link>
                      <Link
                        to="/breathing"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center justify-center gap-2 w-full px-6 py-3 mb-3 rounded-full text-sm font-medium border transition-colors"
                        style={{ color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.35)', backgroundColor: 'rgba(255,255,255,0.08)' }}
                        data-testid="mobile-exercises-hub-btn"
                      >
                        <Home size={16} />
                        Accueil des exercices
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-full text-sm font-medium border transition-colors"
                        style={{ color: '#FCA5A5', borderColor: 'rgba(252,165,165,0.5)', backgroundColor: 'rgba(220,38,38,0.15)' }}
                        data-testid="mobile-logout-btn"
                      >
                        <LogOut size={16} />
                        Se déconnecter
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
                      className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-full text-sm font-medium border transition-colors"
                      style={{ color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.35)', backgroundColor: 'rgba(255,255,255,0.08)' }}
                      data-testid="mobile-login-btn"
                    >
                      <LogIn size={16} />
                      Se connecter
                    </button>
                  )}
                </div>
                )}
                {!isCabinetHome && (
                <div className="px-4 mt-4 space-y-3">
                  <button
                    onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
                    className="w-full px-6 py-3 rounded-full text-sm font-medium"
                    style={{ backgroundColor: C.sky, color: C.white }}
                    data-testid="mobile-menu-start-exercises"
                  >
                    Commencer les exercices
                  </button>
                  <button
                    onClick={() => { setMobileMenuOpen(false); navigate('/evaluer-respiration'); }}
                    className="w-full px-6 py-3 rounded-full text-sm font-medium"
                    style={{ backgroundColor: C.skyLight, color: C.skyDark }}
                  >
                    Tester ma respiration
                  </button>
                </div>
                )}
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Profile Modal */}
      {profileModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 z-[100]"
          style={{ backgroundColor: 'rgba(15,23,42,0.3)', backdropFilter: 'blur(8px)' }}
          onClick={() => setProfileModalOpen(false)}
        >
          <div
            className="rounded-3xl max-w-md w-full p-10 relative"
            style={{ backgroundColor: C.white, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setProfileModalOpen(false)} 
              className="absolute top-6 right-6 p-2 rounded-full hover:bg-slate-100 transition-colors"
              style={{ color: C.muted }}
            >
              <X size={20} />
            </button>
            <div className="flex flex-col items-center text-center">
              <img 
                src="/julien-jaccard.png" 
                alt="Julien Jaccard" 
                className="w-32 h-32 rounded-full object-cover mb-6" 
                style={{ border: `3px solid ${C.sky}` }} 
              />
              <h2 className="text-2xl font-medium mb-1" style={{ fontFamily: 'Outfit, sans-serif', color: C.ink }}>
                Julien Jaccard
              </h2>
              <p className="text-base mb-6" style={{ color: C.slate }}>Physiothérapeute</p>

              {/* Clinic info + Google Maps */}
              <a
                href="https://maps.app.goo.gl/HycF9Mt3kMd1LHaE6"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-start gap-3 p-4 mb-6 rounded-2xl border transition-colors text-left group"
                style={{ borderColor: '#E0F2FE', backgroundColor: '#F0F9FF' }}
                data-testid="clinic-info-maps-link"
              >
                <MapPin size={20} style={{ color: C.sky }} className="flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm leading-tight" style={{ color: C.ink }}>
                    Physiothérapie Les Mains Nuages
                  </p>
                  <p className="text-xs mt-0.5 leading-snug" style={{ color: C.slate }}>
                    Rue des Colombes 11<br />
                    1868 Collombey<br />
                    Valais, Suisse
                  </p>
                  <p className="text-xs mt-1.5 flex items-center gap-1 font-medium group-hover:underline" style={{ color: C.skyDark }}>
                    Voir sur Google Maps <ExternalLink size={11} />
                  </p>
                </div>
              </a>

              <a
                href="mailto:julienjaccard@mains-nuages.ch"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-sm font-medium transition-all hover:shadow-lg"
                style={{ backgroundColor: C.sky, color: C.white }}
              >
                <Mail size={18} />
                julienjaccard@mains-nuages.ch
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SiteHeaderV2;
